import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithTextGetter
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Message } from 'semantic-ui-react';
import { getAllCompanies } from 'services/companyServices';
import { getActiveInterventions } from 'services/interventionServices';
import { fetchAllProducts } from 'services/productServices';
import { getAllQuestionSets } from 'services/questionSetsServices';
import { editUpsertTenant, getUpsertTenantConfig } from 'services/upsertTenantServices';
import { CompanyDto, InterventionDto, ProductDto, QuestionSetDto, TenantConfigUpsertDto } from 'ts-types/api.types';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import { required } from 'utils/validatorUtils';

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  pageDataLoaded: boolean,
  tenantId?: number,
  tenantData?: TenantConfigUpsertDto,
  companies: Array<CompanyDto>,
  companyOptions: Array<DropdownOption>,
  questionSets: Array<QuestionSetDto>,
  questionSetOptions: Array<DropdownOption>,
  interventions: Array<InterventionDto>,
  interventionOptions: Array<DropdownOption>,
  products: Array<ProductDto>,
  productsOptions: Array<DropdownOption>,
  cancelTokenSource: CancelTokenSource,
  saveSuccess: boolean,
  errorMessage?: string
}

class TenantConfigView extends Component<Props, State> {

  private mounted: boolean = false;

  private initialValues: Partial<TenantConfigUpsertDto> = {};

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      pageDataLoaded: false,
      tenantId: undefined,
      companies: [],
      companyOptions: [],
      questionSets: [],
      questionSetOptions: [],
      interventions: [],
      interventionOptions: [],
      products: [],
      productsOptions: [],
      saveSuccess: false,
      cancelTokenSource
    }
    this.fetchData();
  }

  fetchData = async () => {

    const {cancelTokenSource} = this.state;
    const {language, getMessage, currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    this.setState({
      pageDataLoaded: false
    })
    try {
      const tenantData = await getUpsertTenantConfig(cancelTokenSource);
      const companies = await getAllCompanies(mandateId, cancelTokenSource);
      const questionSets = await getAllQuestionSets(cancelTokenSource);
      const interventions = await getActiveInterventions(cancelTokenSource);
      const products = await fetchAllProducts(false, cancelTokenSource);

      this.initialValues = {...tenantData};

      const productOptions = products.map(p => {
        const type = getMessage(`enum.InterventionExternalCode.${p.externalCode}`)

        const descMap: { [lang: string]: string } = {
          "de": p.description,
          "en": p.descriptionEn || p.description,
          "fr": p.descriptionFr || p.description,
          "it": p.descriptionIt || p.description
        };

        return {
          key: p.id,
          value: p.id,
          text: `${type}, ${p.id}, ${descMap[language]}`
        }
      });

      this.setState({
        tenantData: tenantData,
        companies: companies ? companies : [],
        companyOptions: mapToDropdownOptionArray(
            companies, ["description", "city"], "id", "id"),
        questionSets: questionSets ? questionSets : [],
        questionSetOptions: mapToDropdownOptionArrayWithTextGetter(questionSets, questionSet => {
          const nameMap: { [lang: string]: string } = {
            "de": questionSet.name,
            "en": questionSet.nameEn || questionSet.name,
            "fr": questionSet.nameFr || questionSet.name,
            "it": questionSet.nameIt || questionSet.name
          };
          return nameMap[language];
        }),
        interventions: interventions ? interventions : [],
        interventionOptions: mapToDropdownOptionArrayWithTextGetter(interventions, intervention => {
          const descMap: { [lang: string]: string } = {
            "de": intervention.description,
            "en": intervention.descriptionEn || intervention.description,
            "fr": intervention.descriptionFr || intervention.description,
            "it": intervention.descriptionIt || intervention.description
          };
          return descMap[language]
        }),
        products: products ? products : [],
        productsOptions: productOptions

      });
    } catch (e) {
      this.handleError(e.response.data)
    } finally {
      this.setState({
        pageDataLoaded: true
      })
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  handleError(error: any) {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage('error.general'))
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleSubmit = async (values: Partial<TenantConfigUpsertDto>) => {

    let saveValues: Partial<TenantConfigUpsertDto> = {...values};

    const {cancelTokenSource} = this.state;

    const tenantData = await editUpsertTenant(saveValues, cancelTokenSource)
        .catch((e: any) => this.handleError(e.response.data))

    if (tenantData) {
      this.setState({
        tenantData: tenantData,
        saveSuccess: true
      });
    }

  };

  render(): React.ReactNode {

    if (!this.state.pageDataLoaded) {
      return <></>
    }

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;
    const {
      productsOptions,
      companyOptions,
      questionSetOptions,
      interventionOptions,
      saveSuccess,
      errorMessage
    } = this.state;

    return (
        <PraxStyledSegment>
          <form onSubmit={handleSubmit}>
            <FormContainer>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                {
                    saveSuccess &&
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <Message success onDismiss={() => this.setState({saveSuccess: false})}>
                          <p>
                            {getMessage("tenantConfig.saveSuccess")}
                          </p>
                        </Message>
                      </Grid.Column>
                    </Grid.Row>
                }
                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.requiredChromeExtensionVersion")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="requiredChromeExtensionVersion"
                        component={Input}
                        validate={required}
                        fluid
                        autoFocus
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.requiredHostApplicationVersion")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="requiredHostApplicationVersion"
                        component={Input}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.defaultCompany")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="defaultCompanyId"
                        component={Select}
                        options={companyOptions}
                        validate={required}
                        clearable
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.defaultPoolingProduct")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="defaultPoolingProductId"
                        component={Select}
                        options={productsOptions}
                        validate={required}
                        clearable
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.interventionIdForVaccination")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="interventionIdForVaccination"
                        component={Select}
                        options={interventionOptions}
                        validate={required}
                        clearable
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.questionSetIdForVaccination")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="questionSetIdForVaccination"
                        component={Select}
                        options={questionSetOptions}
                        validate={required}
                        clearable
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.questionSetIdForPcrTest")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="questionSetIdForPcrTest"
                        component={Select}
                        options={questionSetOptions}
                        validate={required}
                        clearable
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.testAndFlyEmailRegistrationEnabled")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="testAndFlyEmailRegistrationEnabled"
                        component={CheckBox}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.testAndFlyCompanyId")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="testAndFlyCompanyId"
                        component={Select}
                        options={companyOptions}
                        validate={required}
                        clearable
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("tenantConfig.positivePcrTestOffsetInWeeks")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="positivePcrTestOffsetInWeeks"
                        component={Input}
                        validate={required}
                        onKeyDown={ensureOnlyNumericInput}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
              <FormButtonsRow>
                <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              </FormButtonsRow>
            </FormContainer>
          </form>
        </PraxStyledSegment>
    );
  };

}

export default withMessages(withAuthContext(TenantConfigView));
